import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, Button, message } from 'antd';
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { PageFormBox } from 'ui/PageFormBox';
import { FormWrapper } from 'ui/FormWrapper';
import { FormSection } from 'ui/FormSection';
import { SectionTitle } from 'ui/SectionTitle';
import { requiredRule } from 'utils/forms/validationRules';
import { FETCHING_STATUS, REQUEST_LOADING } from 'constants/alerts';
import { IntegrationService } from 'services/IntegrationService';
import { KGD_INTEGRATION } from 'constants/integration';
import { AccessControl } from 'components/AccessControl';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import { useDocumentTitle } from 'hooks/useDocumentTitle';

function KGD() {
  useDocumentTitle('Настройки интеграции с ВШЭП КГД');
  useHeaderChanger('Настройки интеграции с ВШЭП КГД');
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchKGDConfig();
  }, []);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        login: config?.global_kgd?.login,
        pwd: config?.global_kgd?.pwd,
        fno_send_enable: config?.fno_kgd?.fno_send_enable,
        fno_send_send_url: config?.fno_kgd?.fno_send_send_url,
        fno_send_sign_url: config?.fno_kgd?.fno_send_sign_url,
        fno_send_service_id: config?.fno_kgd?.fno_send_service_id,
        fno_status_enable: config?.fno_kgd_notify?.fno_status_enable,
        fno_status_send_url: config?.fno_kgd_notify?.fno_status_send_url,
        fno_status_sign_url: config?.fno_kgd_notify?.fno_status_sign_url,
        fno_status_service_id: config?.fno_kgd_notify?.fno_status_service_id,
      });
    }
  }, [config]);

  const fetchKGDConfig = async () => {
    const hide = message.loading(FETCHING_STATUS);

    try {
      const { items } = await IntegrationService.getConfig(KGD_INTEGRATION);
      setConfig(items);
    } catch (err) {
      message.error('Не удалось загрузить данные');
    } finally {
      hide();
    }
  };

  const handleSubmit = async (values) => {
    const {
      login,
      pwd,
      fno_send_enable,
      fno_send_send_url,
      fno_send_sign_url,
      fno_send_service_id,
      fno_status_enable,
      fno_status_send_url,
      fno_status_sign_url,
      fno_status_service_id,
    } = values;

    const payload = {
      global_kgd: {
        login,
        pwd,
      },
      fno_kgd: {
        fno_send_enable,
        fno_send_send_url,
        fno_send_sign_url,
        fno_send_service_id,
      },
      fno_kgd_notify: {
        fno_status_enable,
        fno_status_send_url,
        fno_status_sign_url,
        fno_status_service_id,
      },
    };

    const hide = message.loading(REQUEST_LOADING);

    try {
      const isSuccess = await IntegrationService.saveConfig(
        KGD_INTEGRATION,
        payload
      );

      if (isSuccess) {
        message.success('Успешно сохранено');
      }
    } catch (err) {
      message.error('Не удалось сохранить данные');
    } finally {
      hide();
    }
  };

  return (
    <PageFormBox>
      <FormWrapper style={{ padding: '0 30px' }}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{
            span: LABEL_SPAN,
          }}
          wrapperCol={{ span: CONTENT_SPAN }}
          labelAlign="right"
          onFinish={handleSubmit}
        >
          <FormSection>
            <SectionTitle>
              Глобальные настройки для интеграции с ВШЭП
            </SectionTitle>

            <Form.Item name="login" label="Логин" rules={[requiredRule]}>
              <Input placeholder="Введите логин" />
            </Form.Item>

            <Form.Item name="pwd" label="Пароль" rules={[requiredRule]}>
              <Input.Password
                placeholder="Введите пароль"
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Сервис приема и обработки ФНО</SectionTitle>

            <Form.Item
              name="fno_send_enable"
              label="Интеграция"
              rules={[requiredRule]}
            >
              <Radio.Group>
                <Radio value="1">Включить</Radio>
                <Radio value="0">Выключить</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="fno_send_send_url"
              label="Точка подключения"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="fno_send_sign_url"
              label="Сервис подписания"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="fno_send_service_id"
              label="Ключ сервиса"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите ключ" />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>
              Сервис получения уведомлений и статуса обработки ФНО
            </SectionTitle>

            <Form.Item
              name="fno_status_enable"
              label="Интеграция"
              rules={[requiredRule]}
            >
              <Radio.Group>
                <Radio value="1">Включить</Radio>
                <Radio value="0">Выключить</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="fno_status_send_url"
              label="Точка подключения"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="fno_status_sign_url"
              label="Сервис подписания"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите URL" />
            </Form.Item>

            <Form.Item
              name="fno_status_service_id"
              label="Ключ сервиса"
              rules={[requiredRule]}
            >
              <Input placeholder="Введите ключ" />
            </Form.Item>

            <AccessControl pageId="IntegrationConfig" action="update">
              <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Form.Item>
            </AccessControl>
          </FormSection>
        </Form>
      </FormWrapper>
    </PageFormBox>
  );
}

export { KGD };
